import React from 'react';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { t } from '@sm/intl';
import { Box } from '@wds/box';
import { createURL } from '@sm/utils';
import { IconArrowRight } from '@wds/icons';
import { Typography } from '@wds/typography';
import { INDIVIDUAL_PACKAGES } from '~pricing/lib/constants';
import { PricingData, NavRouteParams, PackageProps } from '~pricing/lib/summaryPackages';

import BackToSummary from '~pricing/components/BackToSummary';
import ROUTE_CONFIG from '~pricing/components/Navigation/routes';
import MainHeading from '~pricing/components/MainHeading';
import Navigation from '~pricing/components/Navigation';
import DetailsPackageHeader from '~pricing/components/DetailsTable/PackageHeader';
import Categories from '~app/pages/Pricing/components/DetailsTable/Categories';
import COPY from '~app/pages/Pricing/copy';
import usePackagesWithCTALabel from '~pricing/hooks/usePackagesWithCTALabel';

import useDetailsStyles from '~pricing/pages/Details/useStyles';
import useEducationalStyles from './useStyles';

export default function EducationalDetailsPage({ packageData, packageSet }: PricingData): JSX.Element {
  // This packageSetOverride is only temporary until GROW-2111 is completed
  const packageSetOverride = INDIVIDUAL_PACKAGES;

  const { Details, Sticky } = useDetailsStyles({
    packageCount: packageSetOverride.length,
  });
  const { ViewPricingLink } = useEducationalStyles();
  const { canonicalPath } = useParams<NavRouteParams>();
  const { packages } = usePackagesWithCTALabel(packageData, packageSetOverride);

  if (!packageData) {
    return <></>;
  }

  const path = canonicalPath === 'educational' ? 'individual' : canonicalPath;
  const viewPricingLinkText = t(COPY.VIEW_PRICING);

  return (
    <>
      <header>
        <BackToSummary url={createURL(`/pricing/${path}`, ROUTE_CONFIG[path].queryString)} />
        <MainHeading>{t(COPY.EDUCATIONAL_HEADING)}</MainHeading>
        <Typography align="center" data-testid="Educational__SubHeading">
          {t(COPY.EDUCATIONAL_SUBHEADING)}{' '}
          <Link className={ViewPricingLink} to="/pricing/individual/details">
            {viewPricingLinkText}
            <IconArrowRight title={viewPricingLinkText} size="sm" />
          </Link>
        </Typography>
        <Navigation />
      </header>
      <Box className={Details}>
        <Box className={Sticky}>
          {packages.map((pricingPackage: PackageProps, index: number) => {
            const { skuCost, details, package: packageName, ctaLabel } = pricingPackage;

            return (
              <DetailsPackageHeader
                key={`DetailsHeader-${packageName}`}
                ctaLabel={ctaLabel}
                skuCosts={skuCost}
                details={details}
                packageName={packageName}
                packageType={canonicalPath}
                index={`${index + 1}`}
              />
            );
          })}
        </Box>
        <Categories displayedPackages={packages} />
      </Box>
    </>
  );
}
